import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Map, Lightbulb, Search, ClipboardList, Code2, Users, Tag, Rocket, Megaphone, TrendingUp, Trophy,
  Check, ArrowRight, Star, ExternalLink, X, CircleDot, Lock, HelpCircle, Crown, BookOpen,
  CheckCircle2, ChevronRight, Sparkles, Clock,
} from "lucide-react";
import { AIResourcesShell } from "../../components/ai/AIResourcesShell";
import { BuilderLogo } from "../../components/BuilderLogo";
import { roadmapCatalog, roadmapProgress, roadmapToggleTask } from "../../lib/api";
import { useAuth } from "../../hooks/useAuth";
import { useMarketplaceUnlock } from "../../hooks/useMarketplaceUnlock";

const ICONS = { lightbulb: Lightbulb, search: Search, clipboard: ClipboardList, code: Code2, users: Users, tag: Tag, rocket: Rocket, megaphone: Megaphone, trending: TrendingUp, trophy: Trophy };
const STATUS_COLOR = { completed: "#22c55e", in_progress: "#D4AF37", not_started: "#d4d4d8" };

const Ring = ({ pct }) => {
  const r = 58, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  return (
    <div className="relative w-40 h-40 mx-auto">
      <svg width="160" height="160" className="-rotate-90">
        <circle cx="80" cy="80" r={r} fill="none" stroke="#eef0f2" strokeWidth="12" />
        <circle cx="80" cy="80" r={r} fill="none" stroke="#22c55e" strokeWidth="12" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ transition: "stroke-dashoffset .8s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-3xl font-extrabold text-[#0F0F0F]">{pct}%</span>
      </div>
    </div>
  );
};

export default function FounderRoadmap() {
  const navigate = useNavigate();
  const { user, requireAuth } = useAuth();
  const { refresh: refreshUnlock } = useMarketplaceUnlock();
  const [catalog, setCatalog] = useState(null);
  const [progress, setProgress] = useState(null);
  const [activeStage, setActiveStage] = useState(null);

  const loadProgress = useCallback(() => { roadmapProgress().then(setProgress).catch(() => {}); }, []);
  useEffect(() => { roadmapCatalog().then(setCatalog).catch(() => {}); }, []);
  useEffect(() => { loadProgress(); }, [loadProgress, user]);

  const statusByKey = useMemo(() => {
    const m = {};
    (progress?.stages || []).forEach((s) => { m[s.key] = s; });
    return m;
  }, [progress]);

  const toggleTask = async (stageKey, idx, done) => {
    const run = async () => {
      try { const p = await roadmapToggleTask(stageKey, idx, done); setProgress(p); refreshUnlock(); }
      catch { toast.error("Could not save progress"); }
    };
    if (user) run(); else requireAuth(run);
  };

  if (!catalog || !progress) {
    return <AIResourcesShell active="roadmap" title="Founder Success Roadmap" icon={Map}><div className="py-24 text-center text-[#687280]">Loading roadmap…</div></AIResourcesShell>;
  }

  const currentKey = (progress.stages.find((s) => s.status !== "completed") || {}).key;

  const extra = (
    <>
      <div className="rounded-2xl border border-gray-100 bg-white p-5">
        <p className="text-[11px] font-bold uppercase tracking-widest text-[#9aa1ab]">Your Roadmap</p>
        <p className="font-display text-2xl font-extrabold text-[#0F0F0F] mt-1">{progress.pct}%</p>
        <p className="text-xs text-[#687280]">{progress.steps_completed} of {progress.steps_total} steps completed</p>
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden mt-2"><div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${progress.pct}%` }} /></div>
      </div>
      <div className="rounded-2xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-5 mt-4">
        <HelpCircle size={20} className="text-[#D4AF37]" />
        <p className="font-display text-lg font-bold mt-2">Need Help?</p>
        <p className="text-sm text-[#c9c39f] mt-1 leading-snug">Access guides, templates and AI prompts for every step.</p>
        <button onClick={() => navigate("/learn")} className="mt-4 w-full bg-[#D4AF37] text-[#0F0F0F] rounded-full py-2.5 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="roadmap-view-guides">View Guides</button>
      </div>
    </>
  );

  return (
    <AIResourcesShell active="roadmap" title="Founder Success Roadmap" subtitle="The proven 10-step roadmap to build, launch, and grow your successful startup." icon={Map} extra={extra}>
      {/* Stepper */}
      <div className="overflow-x-auto pb-3 -mx-1" data-testid="roadmap-stepper">
        <div className="flex items-start min-w-[720px] px-1">
          {catalog.stages.map((s, i) => {
            const st = statusByKey[s.key]?.status || "not_started";
            const color = STATUS_COLOR[st];
            return (
              <div key={s.key} className="flex-1 flex flex-col items-center relative">
                {i < catalog.stages.length - 1 && (
                  <span className="absolute top-4 left-1/2 w-full h-[3px]" style={{ background: st === "completed" ? "#22c55e" : "#eef0f2" }} />
                )}
                <button onClick={() => setActiveStage(s)} className="relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-transform hover:scale-110" style={{ background: color, color: st === "not_started" ? "#71717a" : "#0F0F0F" }} data-testid={`roadmap-step-${s.num}`}>
                  {st === "completed" ? <Check size={16} /> : s.num}
                </button>
                <span className="text-[11px] font-semibold text-[#687280] mt-2 text-center">{s.short}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main grid: cards + rail */}
      <div className="grid lg:grid-cols-[1fr_300px] gap-6 mt-6">
        {/* Stage cards */}
        <div className="space-y-4">
          {catalog.stages.map((s) => {
            const Icon = ICONS[s.icon] || CircleDot;
            const st = statusByKey[s.key]?.status || "not_started";
            const sp = statusByKey[s.key] || { task_done: 0, task_total: s.tasks.length };
            return (
              <div key={s.key} className={`rounded-2xl border bg-white p-5 ${st === "completed" ? "border-green-200" : st === "in_progress" ? "border-[#D4AF37]/40" : "border-gray-100"}`} data-testid={`roadmap-card-${s.key}`}>
                <div className="flex gap-4">
                  <div className="shrink-0 flex flex-col items-center gap-1">
                    <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: st === "completed" ? "#dcfce7" : st === "in_progress" ? "#FBF7EA" : "#f4f4f5", color: STATUS_COLOR[st] === "#d4d4d8" ? "#71717a" : STATUS_COLOR[st] }}>
                      {st === "completed" ? <CheckCircle2 size={22} /> : <Icon size={22} strokeWidth={1.8} />}
                    </span>
                    <span className="text-xs font-bold text-[#9aa1ab]">{s.num}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-lg font-bold text-[#0F0F0F]">{s.title}</h3>
                      {s.star && <Star size={15} className="text-[#D4AF37] fill-[#D4AF37]" />}
                    </div>
                    <p className="text-sm text-[#687280] mt-0.5">{s.desc}</p>
                    <p className="text-[11px] text-[#9aa1ab] mt-2 flex items-center gap-1"><Clock size={11} /> Est. Time: {s.est_time}</p>
                  </div>
                  <div className="hidden sm:block w-40 shrink-0 border-l border-gray-100 pl-4">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-[#16a34a]">Key Outcome</p>
                    <p className="text-xs text-[#687280] mt-1 leading-snug">{s.outcome}</p>
                  </div>
                  <button onClick={() => setActiveStage(s)} className="self-center shrink-0 inline-flex items-center gap-1.5 border border-gray-200 rounded-full px-4 py-2 text-xs font-bold text-[#374151] hover:border-[#D4AF37]" data-testid={`roadmap-view-${s.key}`}>
                    View Step <ArrowRight size={13} />
                  </button>
                </div>
                {sp.task_total > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-2">
                    <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.round((sp.task_done / sp.task_total) * 100)}%`, background: STATUS_COLOR[st] }} /></div>
                    <span className="text-[11px] font-semibold text-[#9aa1ab]">{sp.task_done}/{sp.task_total} tasks</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right rail */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-100 bg-white p-5" data-testid="roadmap-progress-rail">
            <p className="font-display font-bold text-[#0F0F0F] text-center">Your Roadmap Progress</p>
            <Ring pct={progress.pct} />
            <p className="text-center text-sm font-semibold text-[#0F0F0F]" data-testid="roadmap-steps-completed">{progress.steps_completed} of {progress.steps_total} Steps Completed</p>
            <div className="mt-4 space-y-2 text-sm">
              <Legend color="#22c55e" label="Completed" n={progress.steps_completed} />
              <Legend color="#D4AF37" label="In Progress" n={progress.in_progress} />
              <Legend color="#d4d4d8" label="Not Started" n={progress.not_started} />
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-5">
            <p className="font-display font-bold text-[#0F0F0F] mb-3">What You'll Get</p>
            <ul className="space-y-2.5 text-sm text-[#374151]">
              {["Proven 10-step process", "Curated tools & platforms", "Actionable checklists", "AI prompts & templates", "Time estimates", "Success tips & guidance"].map((x) => (
                <li key={x} className="flex items-center gap-2"><Check size={15} className="text-[#D4AF37] shrink-0" /> {x}</li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-5">
            <p className="font-display font-bold text-[#0F0F0F] mb-3">Deployment & Distribution</p>
            <div className="space-y-3">
              {catalog.deployment.map((d) => (
                <div key={d.name} className="rounded-xl border border-gray-100 p-3" data-testid={`roadmap-deploy-${d.icon}`}>
                  <div className="flex items-center gap-2">
                    <BuilderLogo url={d.url} name={d.name} size={30} />
                    <p className="font-semibold text-sm text-[#0F0F0F]">{d.name}</p>
                  </div>
                  <p className="text-xs text-[#687280] mt-1.5">{d.desc}</p>
                  <a href={d.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center justify-center gap-1.5 w-full border border-gray-200 rounded-full py-2 text-xs font-bold text-[#374151] hover:border-[#D4AF37]">Open Guide <ExternalLink size={12} /></a>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-[#FBF7EA] border border-[#D4AF37]/30 p-5">
            <p className="font-display font-bold text-[#0F0F0F] flex items-center gap-2"><Sparkles size={16} className="text-[#D4AF37]" /> Need Help at Any Step?</p>
            <p className="text-xs text-[#687280] mt-1.5">Get personalized guidance, resources and AI support whenever you need it.</p>
            <button onClick={() => navigate("/ai-resources")} className="mt-3 inline-flex items-center justify-center gap-1.5 w-full bg-[#0F0F0F] text-white rounded-full py-2.5 text-xs font-bold hover:bg-[#1c1c1c]" data-testid="roadmap-explore-resources">Explore Resources <ArrowRight size={13} /></button>
          </div>
        </div>
      </div>

      {/* Popular tools */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-bold text-[#0F0F0F]">Popular Tools Across the Roadmap</h3>
            <p className="text-sm text-[#687280]">Top platforms used by successful founders.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-6 items-center">
          {catalog.popular_tools.map((t) => (
            <a key={t.name} href={t.url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1.5 group" data-testid={`roadmap-popular-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <BuilderLogo url={t.url} name={t.name} size={48} />
              <span className="text-[11px] text-[#687280] group-hover:text-[#0F0F0F]">{t.name}</span>
            </a>
          ))}
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="mt-10 rounded-2xl bg-gradient-to-r from-[#FBF7EA] to-[#FDF6E3] border border-[#D4AF37]/30 p-6 flex flex-col sm:flex-row items-center gap-4">
        <span className="w-12 h-12 rounded-2xl bg-[#D4AF37] text-[#0F0F0F] flex items-center justify-center shrink-0"><Crown size={22} /></span>
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <p className="font-display text-lg font-extrabold text-[#0F0F0F]">Stay on Track. Build with Confidence.</p>
          <p className="text-sm text-[#687280]">Your roadmap adapts as you progress. Complete each step and LaunchBS will guide you to the next.</p>
        </div>
        <button onClick={() => navigate("/notebook")} className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#c5a030] shrink-0" data-testid="roadmap-cta-dashboard">Go to My Dashboard <ArrowRight size={15} /></button>
      </div>

      {activeStage && (
        <StepModal stage={activeStage} sp={statusByKey[activeStage.key]} current={currentKey === activeStage.key} onClose={() => setActiveStage(null)} onToggle={toggleTask} navigate={navigate} />
      )}
    </AIResourcesShell>
  );
}

const Legend = ({ color, label, n }) => (
  <div className="flex items-center justify-between">
    <span className="flex items-center gap-2 text-[#687280]"><span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} /> {label}</span>
    <span className="font-bold text-[#0F0F0F]">{n}</span>
  </div>
);

const DEPLOY_TARGETS = [
  { name: "Apple Developer", desc: "Build, test and publish your iOS app.", url: "https://developer.apple.com" },
  { name: "Google Play Console", desc: "Publish and grow your app on Google Play.", url: "https://play.google.com/console" },
];

const StepModal = ({ stage, sp, onClose, onToggle, navigate }) => {
  const Icon = ICONS[stage.icon] || CircleDot;
  const done = new Set(sp?.done_tasks || []);
  const [showDeploy, setShowDeploy] = useState(false);
  const openTool = (t) => {
    if (t.coming_soon) { toast.info(`${t.label} is coming soon.`); return; }
    if (t.action === "deploy") { setShowDeploy(true); return; }
    if (t.action === "blueprint") { window.dispatchEvent(new CustomEvent("lbs:open-blueprint", { detail: { mode: "have_idea" } })); onClose(); return; }
    if (t.to) navigate(t.to);
  };
  return (
    <div className="fixed inset-0 z-[95] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="roadmap-step-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-2xl bg-[#FBF7EA] text-[#D4AF37] flex items-center justify-center"><Icon size={22} /></span>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#9aa1ab]">Step {stage.num} · {stage.est_time}</p>
              <h2 className="font-display text-2xl font-extrabold text-[#0F0F0F] flex items-center gap-2">{stage.title} {stage.star && <Star size={16} className="text-[#D4AF37] fill-[#D4AF37]" />}</h2>
            </div>
          </div>
          <button onClick={onClose} className="text-[#687280] hover:text-[#0F0F0F] shrink-0 ml-2" data-testid="roadmap-step-close"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#687280] mt-3">{stage.desc}</p>

        {/* Tasks */}
        <div className="mt-5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#8a6d1a] mb-2">Tasks</p>
          <div className="space-y-2">
            {stage.tasks.map((t, i) => {
              const isDone = done.has(i);
              return (
                <button key={i} onClick={() => onToggle(stage.key, i, !isDone)} className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${isDone ? "border-green-200 bg-green-50/50" : "border-gray-100 hover:border-[#D4AF37]/50"}`} data-testid={`roadmap-task-${stage.key}-${i}`}>
                  <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${isDone ? "bg-green-500 text-white" : "border-2 border-gray-300"}`}>{isDone && <Check size={14} />}</span>
                  <span className={`text-sm ${isDone ? "text-[#687280] line-through" : "text-[#0F0F0F]"}`}>{t}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* LaunchBS tools */}
        <div className="mt-5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#8a6d1a] mb-2">LaunchBS Tools</p>
          <div className="flex flex-wrap gap-2">
            {stage.launchbs.map((t) => (
              <button key={t.label} onClick={() => openTool(t)} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-bold transition-colors ${t.coming_soon ? "bg-gray-100 text-[#9aa1ab]" : "bg-[#0F0F0F] text-white hover:bg-[#1c1c1c]"}`} data-testid={`roadmap-lbtool-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {t.coming_soon ? <Lock size={12} /> : t.action === "deploy" ? <Rocket size={12} className="text-[#D4AF37]" /> : <ChevronRight size={12} />} {t.label}{t.coming_soon ? " · Soon" : ""}
              </button>
            ))}
          </div>
        </div>

        {/* External tools */}
        <div className="mt-5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#8a6d1a] mb-2">External Tools</p>
          <div className="flex flex-wrap gap-2">
            {stage.external.map((t) => (
              <a key={t.label} href={t.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-[#374151] hover:border-[#D4AF37]" data-testid={`roadmap-ext-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <BuilderLogo url={t.url} name={t.label} size={18} tile={false} /> {t.label} <ExternalLink size={11} />
              </a>
            ))}
          </div>
        </div>

        {/* Outcome */}
        <div className="mt-5 rounded-2xl bg-[#0F0F0F] text-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#D4AF37] mb-1">Key Outcome</p>
          <p className="text-sm text-[#e8e6df] flex items-center gap-2"><CheckCircle2 size={15} className="text-green-400 shrink-0" /> {stage.outcome}</p>
        </div>
      </div>

      {/* Deploy App sub-modal */}
      {showDeploy && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setShowDeploy(false); }} data-testid="roadmap-deploy-modal">
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-2xl bg-[#FBF7EA] text-[#D4AF37] flex items-center justify-center"><Rocket size={20} /></span>
                <p className="font-display text-xl font-extrabold text-[#0F0F0F]">Deploy Your App</p>
              </div>
              <button onClick={() => setShowDeploy(false)} className="text-[#687280] hover:text-[#0F0F0F]" data-testid="roadmap-deploy-close"><X size={20} /></button>
            </div>
            <p className="text-sm text-[#687280] mt-3">Publish your app to the stores. Choose a platform to get started:</p>
            <div className="mt-4 space-y-2.5">
              {DEPLOY_TARGETS.map((d) => (
                <a key={d.name} href={d.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-2xl border border-gray-100 p-4 hover:border-[#D4AF37] transition-colors group" data-testid={`roadmap-deploy-target-${d.name.split(" ")[0].toLowerCase()}`}>
                  <BuilderLogo url={d.url} name={d.name} size={40} />
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-[#0F0F0F]">{d.name}</p>
                    <p className="text-xs text-[#687280]">{d.desc}</p>
                  </div>
                  <ExternalLink size={16} className="text-[#9aa1ab] group-hover:text-[#D4AF37] shrink-0" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
